import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import type { IOdds, IPriceOption } from '@/interface/IOdds';
import type { ITranslation } from '@/interface/ITranslation';
import { MarketType } from '@/interface/enum';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { i18n } from '@/libraries/i18n';

export type IPriceOptionDisplay = IPriceOption & {
    totalHomeScore: number;
    totalAwayScore: number;
}

function getOptionTranslated(key: string, option = {}) {
    return i18n.t(`odds_option_${key}`, option);
}

export function groupCorrectScore(odds: IOdds, liveHomeScore: number, liveAwayScore: number) {
    const homeKey = getOptionTranslated('home').toString();
    const awayKey = getOptionTranslated('away').toString();
    const drawKey = getOptionTranslated('draw').toString();

    const priceOptions = odds.priceOption.map<IPriceOptionDisplay>((price) => {
        const [home, away] = price.option
            .split(':')
            .map(x => Number.parseInt(x));

        const totalHomeScore = odds.marketType === MarketType.SH_CorrectScore ? home : home + liveHomeScore;
        const totalAwayScore = odds.marketType === MarketType.SH_CorrectScore ? away : away + liveAwayScore;

        return {
            ...price,
            totalHomeScore,
            totalAwayScore,
        };
    });

    const groupFn = (price: IPriceOptionDisplay) => {
        const { totalHomeScore, totalAwayScore } = price;
        if (totalHomeScore > totalAwayScore) return homeKey;
        if (totalHomeScore < totalAwayScore) return awayKey;
        return drawKey;
    };

    // sort first to ensure the key order is [home | draw | away]
    const sorted = orderBy(
        priceOptions,
        price => price.totalAwayScore - price.totalHomeScore,
    );
    const grouped = groupBy(sorted, groupFn);
    grouped[homeKey] = orderBy(grouped[homeKey], [
        price => price.totalHomeScore,
        price => price.totalAwayScore,
    ]);
    grouped[drawKey] = orderBy(grouped[drawKey], [
        price => price.totalHomeScore,
        price => price.totalAwayScore,
    ]);
    grouped[awayKey] = orderBy(grouped[awayKey], [
        price => price.totalAwayScore,
        price => price.totalHomeScore,
    ]);

    return Object.values(grouped);
}

export function groupFgLg(odds: IOdds) {
    const sorted = orderBy(odds.priceOption, (price) => {
        const sortOrder = ['F', 'L', 'G'];
        return sortOrder.indexOf(price.option.slice(-1));
    });
    return Object.values(
        groupBy(sorted, (price) => {
            const groupNames: Record<string, string> = {
                F: getOptionTranslated('firstGoal'),
                L: getOptionTranslated('lastGoal'),
                G: getOptionTranslated('noGoal'),
            };
            return groupNames[price.option.slice(-1)];
        }),
    );
}

export function groupHtft(odds: IOdds) {
    const sorted = orderBy(odds.priceOption, (price) => {
        const sortOrder = ['H', 'D', 'A'];
        return sortOrder.indexOf(price.option.slice(0, 1));
    });
    return Object.values(
        groupBy(sorted, price => price.option.slice(0, 1)),
    );
}

export function groupAsia1X2(odds: IOdds, homeTeamName: ITranslation[], awayTeamName: ITranslation[]) {
    const sorted = orderBy(odds.priceOption, (price) => {
        const sortOrder = ['1', 'x', '2'];
        return sortOrder.indexOf(price.option) - 1;
    });
    return groupBy(sorted, (price) => {
        const extraInfo = price.optionName.split(';');
        const key = extraInfo[0].replace('odds_option_', '');
        const homeTeam = getTranslated(homeTeamName);
        const awayTeam = getTranslated(awayTeamName);
        const option = {
            team: extraInfo[1] === 'home'
                ? homeTeam
                : awayTeam,
            goals: extraInfo[2],
        };
        return getOptionTranslated(key, option);
    });
}
